import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function MarketSegment() {
  const marketSegments = [
    {
      title: "Chocolate Manufacturers",
      description: "We supply high-grade Nigerian cocoa beans to global chocolate brands and craft chocolate makers who seek rich flavor profiles, ethical sourcing, and fully traceable supply chains they can trust.",
      icon: "ri-cake-3-line",
      image: "https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/61b4b9670b22ccd24c0dc368eb44200b.png",
      benefits: ["Rich flavor profiles", "Ethical sourcing", "Full traceability", "Premium quality"]
    },
    {
      title: "Cocoa Processors & Ingredient Suppliers",
      description: "Our bulk cocoa beans are perfectly suited for processing into cocoa butter, liquor, cake, and powder, meeting the raw material needs of food ingredient companies and processing factories around the world.",
      icon: "ri-settings-4-line",
      image: "https://readdy.ai/api/search-image?query=Large-scale%20cocoa%20processing%20facility%20with%20industrial%20machinery%20transforming%20cocoa%20beans%20into%20cocoa%20butter%20liquor%20cake%20and%20powder%2C%20modern%20food%20processing%20equipment%20in%20clean%20sterile%20environment%2C%20bulk%20processing%20operations%20with%20quality%20control%20systems%2C%20professional%20industrial%20setting&width=400&height=280&seq=proc_supp_002&orientation=landscape",
      benefits: ["Bulk supply capabilities", "Processing-grade quality", "Global distribution", "Consistent supply"]
    },
    {
      title: "Ethical & Certified Commodity Traders",
      description: "We supply traders and cooperatives seeking Rainforest Alliance–certified and EUDR-compliant cocoa for redistribution across international markets, with full documentation and traceability guaranteed.",
      icon: "ri-global-line",
      image: "https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/b2d1c080999f014799765c96280c7617.png",
      benefits: ["Rainforest Alliance certified", "EUDR compliance", "Full documentation", "Global redistribution"]
    },
    {
      title: "Specialty & Organic Cocoa Buyers",
      description: "For niche markets focused on single-origin and sustainably sourced cocoa, we provide fully traceable beans backed by farm-level documentation and rigorous quality assurance, ideal for craft chocolate makers and ethical brands.",
      icon: "ri-leaf-line",
      image: "https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/20b50dbad5e5b1de56c391f84de681e5.png",
      benefits: ["Single-origin quality", "Sustainable sourcing", "Farm-level documentation", "Craft chocolate grade"]
    },
    {
      title: "Government and Development Partners",
      description: "We collaborate with institutions and development partners focused on sustainable agriculture, supply chain innovation, and community empowerment within the cocoa sector, aligning our work with shared impact goals.",
      icon: "ri-community-line",
      image: "https://readdy.ai/api/search-image?query=Development%20partnership%20meeting%20with%20government%20officials%20and%20agricultural%20experts%20discussing%20sustainable%20cocoa%20farming%20initiatives%2C%20conference%20room%20setting%20with%20charts%20and%20maps%20of%20cocoa%20growing%20regions%2C%20collaborative%20planning%20session%20focused%20on%20community%20empowerment%20and%20supply%20chain%20innovation&width=400&height=280&seq=gov_dev_005&orientation=landscape",
      benefits: ["Sustainable agriculture focus", "Supply chain innovation", "Community empowerment", "Shared impact goals"]
    }
  ];

  const stats = [
    { number: "70,000+", label: "Metric Tonnes Annually", icon: "ri-scales-3-line" },
    { number: "5", label: "Key Market Segments", icon: "ri-pie-chart-line" },
    { number: "30+", label: "Countries Served", icon: "ri-earth-line" },
    { number: "100%", label: "Traceability Guaranteed", icon: "ri-shield-check-line" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-24 px-4 text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(34, 197, 94, 0.8), rgba(245, 158, 11, 0.8)), url('https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/75cf671f60d9113ef020287a1b2a4956.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto max-w-4xl text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-pie-chart-2-line text-3xl text-white"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Market Segments
          </h1>
          <p className="text-xl md:text-2xl mb-8 leading-relaxed max-w-3xl mx-auto">
            At Olatunde International Limited, we specialize exclusively in the export of premium Nigerian cocoa beans, tailored to meet the diverse needs of clients across the global cocoa and chocolate value chain.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/services" 
              className="bg-white text-green-700 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Our Services
            </Link>
            <Link 
              to="/why-choose-us" 
              className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-green-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Why Choose Us
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-2xl text-amber-600`}></i>
                </div>
                <div className="text-3xl md:text-4xl font-bold text-green-800 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Segments Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-green-800 mb-6">
              Our Market Segments
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From manufacturers to traders, our operations are built on quality, compliance, and consistency, delivering reliable supply to every segment we serve.
            </p>
          </div>

          <div className="space-y-16">
            {marketSegments.map((segment, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
                <div className="lg:w-1/2">
                  <div className="relative">
                    <img 
                      src={segment.image} 
                      alt={segment.title}
                      className="w-full h-80 object-cover rounded-2xl shadow-lg"
                    />
                    <div className="absolute top-6 left-6 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <i className={`${segment.icon} text-2xl text-green-600`}></i>
                    </div>
                  </div>
                </div>
                
                <div className="lg:w-1/2">
                  <h3 className="text-3xl md:text-4xl font-bold text-green-800 mb-6">
                    {segment.title}
                  </h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    {segment.description}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {segment.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Link 
                    to="/services" 
                    className="inline-flex items-center bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Learn More
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Assurance Section */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-green-800 mb-6">
              Quality Across All Segments
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Regardless of the market segment, we maintain the highest standards of quality, compliance, and traceability in every shipment.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-4">Quality Control</h3>
              <p className="text-gray-600">
                Rigorous quality protocols at every stage ensure consistent, export-grade cocoa beans for all market segments.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-shield-2-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-4">Full Compliance</h3>
              <p className="text-gray-600">
                Complete adherence to international standards including EUDR, Rainforest Alliance, and other certifications.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-route-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-4">End-to-End Traceability</h3>
              <p className="text-gray-600">
                Complete supply chain visibility from farm to export, with digital tools ensuring full traceability for all clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-amber-600 text-white">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our global network of satisfied clients across all market segments. Experience the Olatunde International difference in quality, compliance, and reliability.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/services" 
              className="bg-white text-green-700 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              View Our Services
            </Link>
            <Link 
              to="/about" 
              className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-green-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us Today
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
